import { Skeleton } from '@mui/material';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { EmptyStateCard } from '../../components/EmptyStateCard';
import { TableDataPreview } from '../../components/TableDataPreview';
import { TableSelector } from '../../components/TableSelector';
import { TablesSectionCard } from '../../components/TablesSectionCard';
import { useTablesSection } from '../../hooks/useTablesSection';
import type { TablesSectionProps } from '../../types';

export const TablesSection = ({ isLoading = false }: TablesSectionProps) => {
  const {
    hasTables,
    emptyTitle,
    emptyBody,
    options,
    selectedId,
    onSelect,
    isLoading: showSkeleton,
    selectedTable,
    selectedTableNumber,
    isAnalyticalDatasetSelected,
    selectedDatasetSources,
    sources,
    projectId,
    addVariableLabel,
    isVariablePickerOpen,
    onAddVariable,
    onCloseVariablePicker,
    isEditDisabled,
    editLockTooltip,
    analyticalDatasetProfileId,
    hasAnalyticalDatasetResults,
    datasetSourcesMissingFromRun,
    datasetView,
    onSelectDatasetView,
    listViewLabel,
    tableViewLabel,
    datasetEmptyHint,
    analyticalDatasetCount,
  } = useTablesSection(isLoading);

  // Both the real results grid and its pre-processed empty placeholder are
  // DataGrid-shaped and want the same fixed-height card.
  const isDatasetTableView = isAnalyticalDatasetSelected && datasetView === 'table';

  return !showSkeleton && !hasTables ? (
    <EmptyStateCard title={emptyTitle} body={emptyBody} data-testid="analysis-tables-empty" />
  ) : (
    <TablesSectionCard fillHeight={isDatasetTableView}>
      {showSkeleton ? (
        <Skeleton
          variant="rounded"
          height={40}
          width={320}
          animation="wave"
          sx={{ borderRadius: `${borderRadius.md}px` }}
          data-testid="analysis-tables-loading"
        />
      ) : (
        <>
          <TableSelector options={options} selectedId={selectedId} onChange={onSelect} />
          <TableDataPreview
            selectedTable={selectedTable}
            selectedTableNumber={selectedTableNumber}
            isAnalyticalDatasetSelected={isAnalyticalDatasetSelected}
            selectedDatasetSources={selectedDatasetSources}
            analyticalDatasetCount={analyticalDatasetCount}
            sources={sources}
            projectId={projectId}
            addVariableLabel={addVariableLabel}
            isVariablePickerOpen={isVariablePickerOpen}
            onAddVariable={onAddVariable}
            onCloseVariablePicker={onCloseVariablePicker}
            isEditDisabled={isEditDisabled}
            editLockTooltip={editLockTooltip}
            analyticalDatasetProfileId={analyticalDatasetProfileId}
            hasAnalyticalDatasetResults={hasAnalyticalDatasetResults}
            datasetSourcesMissingFromRun={datasetSourcesMissingFromRun}
            datasetView={datasetView}
            onSelectDatasetView={onSelectDatasetView}
            listViewLabel={listViewLabel}
            tableViewLabel={tableViewLabel}
            datasetEmptyHint={datasetEmptyHint}
          />
        </>
      )}
    </TablesSectionCard>
  );
};
